import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosResponseHeaders } from "axios";
import { saveAs } from "file-saver";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { PendingBillsSummary } from "../../../../api/services/reports/models";
import {
  useExportPendingBillingSummeryReport,
  useGetPendingBillingReports,
} from "../../../../api/services/reports/reportsApi";
import { MuiBox } from "../../../../components";
import {
  FORM_ERROR_TIMEOUT_LONG,
  PENDING_BILLING_REPORT_TYPE_SITE_CORE_KEY_VALUE,
} from "../../../../helpers/constants";
import { isNotEmpty } from "../../../../helpers/utils";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { getFileNameFromHeader } from "../../helper";
import { PendingBillingSearchFormKeys } from "../../modelsAndSchemas/models";
import { getPendingBillingSearchFormSchema } from "../../modelsAndSchemas/ValidationSchema";
import { PendingBillingSearchForm } from "../pendingBillingSearchForm/PendingBillingSearchForm";
import { PendingBillingReportsList } from "../pendingBillingSearchResult/PendingBillingSearchResult";

const DEFAULT_OUTSTANDING_AMOUNT = "0.01"; // default value is set to 0.01 to fetch the pending billings with outstanding amount greater than 0. This is done to avoid fetching the billings with 0 outstanding amount which are not relevant for the pending billing report.
export const PendingBillingSearch = () => {
  const {
    commonValidationMessages: { requiredValidation },
    commonLabels: { someThingWentWrongPleaseTryAgainLater },
  } = useSiteCore();
  const methods = useForm<PendingBillingSearchFormKeys>({
    defaultValues: {
      officeType: PENDING_BILLING_REPORT_TYPE_SITE_CORE_KEY_VALUE.myOffices,
      includeClosed:
        PENDING_BILLING_REPORT_TYPE_SITE_CORE_KEY_VALUE.includeClosedNo,
      clientIds: "",
      outStandingAmount: DEFAULT_OUTSTANDING_AMOUNT, // default value is set to 0.01 to fetch the pending billings with outstanding amount greater than 0. This is done to avoid fetching the billings with 0 outstanding amount which are not relevant for the pending billing report.
    },
    resolver: yupResolver(
      getPendingBillingSearchFormSchema(requiredValidation),
    ),
  });
  const [state, setState] = useState<{
    pendingBillingList: PendingBillsSummary[];
  }>({
    pendingBillingList: [],
  });

  const {
    mutateAsync: getPendingBillingReports,
    data: pendingBillingList,
    isPending: isPendingBillingListLoading,
    isSuccess: isGetPendingBillingListSuccess,
    isError: isGetPendingBillingListError,
    isFiltered: isPendingBillingListFiltered,
    filterParams: pendingBillingListFilteredParams,
  } = useGetPendingBillingReports();

  const {
    mutateAsync: exportPendingBillingReport,
    isPending: isExportPendingBillingReportLoading,
    isError: isExportPendingBillingReportError,
  } = useExportPendingBillingSummeryReport();

  const dispatch = useDispatch();
  const pendingBillingSearchFormRef = useRef<{ reset: () => void }>(null);

  const isPendingBillingApiLoading =
    isPendingBillingListLoading || isExportPendingBillingReportLoading;
  const isPendingBillingApiError =
    isGetPendingBillingListError || isExportPendingBillingReportError;

  useEffect(() => {
    if (isPendingBillingApiLoading) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    } else {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isPendingBillingApiLoading]);

  useEffect(() => {
    if (isGetPendingBillingListSuccess) {
      setState((prevState) => ({
        ...prevState,
        pendingBillingList: pendingBillingList?.data ?? [],
      }));
    }
  }, [isGetPendingBillingListSuccess]);

  useEffect(() => {
    if (isPendingBillingApiError) {
      dispatch(
        toggleToast({
          open: true,
          message: someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isPendingBillingApiError]);

  const handleContinueClick = () => {
    const { officeType, includeClosed, clientIds, outStandingAmount } =
      getValues();

    getPendingBillingReports({
      reportType: officeType,
      includeClosed: includeClosed,
      clientIds: clientIds ? [clientIds] : undefined,
      amountFilter: {
        minAmount: outStandingAmount ?? DEFAULT_OUTSTANDING_AMOUNT,
      },
    });
  };

  const {
    getValues,
    handleSubmit,
    formState: { errors },
  } = methods;
  const handleSearchFormSubmit = () => {
    // Added timeout as zero validation has a delay to FORM_ERROR_TIMEOUT_SHORT.
    setTimeout(() => {
      if (isNotEmpty(errors)) return;
      handleSubmit(handleContinueClick)();
    }, FORM_ERROR_TIMEOUT_LONG);
  };

  const handleExport = async () => {
    const response = await exportPendingBillingReport(
      pendingBillingListFilteredParams,
    );
    const filename = getFileNameFromHeader(
      response.headers as AxiosResponseHeaders,
    );
    saveAs(response.data, filename);
  };

  const onResetSearch = () => {
    setState((prevState) => ({
      ...prevState,
      pendingBillingList: [],
    }));
  };

  return (
    <MuiBox>
      <FormProvider {...methods}>
        <PendingBillingSearchForm
          ref={pendingBillingSearchFormRef}
          onResetSearch={onResetSearch}
          onSubmit={handleSearchFormSubmit}
          defaultOutStandingAmount={DEFAULT_OUTSTANDING_AMOUNT}
        />
      </FormProvider>
      <PendingBillingReportsList
        onExport={handleExport}
        isFilteredPendingBillings={isPendingBillingListFiltered}
        pendingBillings={state.pendingBillingList}
      />
    </MuiBox>
  );
};
